import { Element, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Action, {
  ACTIONS,
} from "@/components/editor/primitives/legacy/action/action-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";
import useViewport from "@/hooks/use-viewport";
import getViewportValue from "@/utils/get-viewport-value";

import {
  BackgroundWrapper,
  ContentWrapper,
  CTAWrapper,
  Logo,
  LogoWrapper,
  RichTextContainer,
  TimeAndDateItem,
  TimeAndDateWrapper,
} from "./vr-header-component-styles";

export type HeaderProps = {
  backgroundImage?: string;
  backgroundTint?: string;
  backgroundPosition?: ImagePosition;
  logo?: string;
  isLogoShowing?: boolean;
  initialHeading?: string;
  initialSubheading?: string;
  initialCtaText?: [string?, string?];
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => (
  <HeaderSettings optimalImageSize="1440×800px" disableBackgroundImageAlt />
);

export default function VRHeader(props: HeaderProps) {
  const {
    id,
    connectors: { connect },
  } = useNode();

  const viewport = useViewport();

  const isRichTextCompatible = useRichTextCompatible(id, [
    "heading",
    "subheading",
    "date",
    "time",
  ]);

  const [startTime, endTime] = props.initialCtaText || [];
  const backgroundWrapperStyle = React.useMemo(() => {
    return {
      padding: getViewportValue(
        [
          `${rem(props.isLogoShowing ? 278 : 100)} ${rem(24)} ${rem(100)} ${rem(
            24
          )}`,
          `${rem(props.isLogoShowing ? 278 : 100)} ${rem(24)}`,
          `${rem(278)} ${rem(80)} ${rem(230)} ${rem(80)}`,
        ],
        viewport
      ),
    };
  }, [props.isLogoShowing, viewport]);

  return (
    <BackgroundWrapper
      id={id}
      ref={connect}
      data-testid="vr-header-wrapper"
      backgroundImage={props.backgroundImage}
      backgroundTint={props.backgroundTint}
      backgroundPosition={props.backgroundPosition}
      imageFit={props.imageFit}
      style={backgroundWrapperStyle}
    >
      {props.logo && props.isLogoShowing && (
        <LogoWrapper isLogoResized={!isNaN(props.logoSize)}>
          <Logo
            data-testid="vr-header-logo"
            src={props.logo}
            style={{
              height: formatLogoSize(props.logoSize),
              width: "auto",
            }}
            alt="Header Logo"
          />
        </LogoWrapper>
      )}
      <ContentWrapper>
        {isRichTextCompatible ? (
          <RichTextContainer>
            <Element
              id="heading"
              is={RichText}
              text={`<h2 style="text-align:center;">${props.initialHeading}</h2>`}
            />
            <Element
              id="subheading"
              is={RichText}
              text={`<p style="text-align:center;">${props.initialSubheading}</p>`}
            />
            <TimeAndDateWrapper>
              <TimeAndDateItem>
                <Element
                  id="date"
                  is={RichText}
                  text={`<p style="text-align:center;">${startTime}</p>`}
                />
              </TimeAndDateItem>
              <TimeAndDateItem>
                <Element
                  id="time"
                  is={RichText}
                  text={`<p style="text-align:center;">${endTime}</p>`}
                />
              </TimeAndDateItem>
            </TimeAndDateWrapper>
          </RichTextContainer>
        ) : (
          <>
            <Element
              id="heading"
              is={Text}
              text={props.initialHeading}
              pattern="headingOne"
            />
            <Element
              id="subheading"
              is={Text}
              text={props.initialSubheading}
              pattern="paragraph"
            />
            <TimeAndDateWrapper>
              <TimeAndDateItem>
                <Element
                  id="date"
                  is={Text}
                  text={startTime}
                  pattern="paragraph"
                />
              </TimeAndDateItem>
              <TimeAndDateItem>
                <Element
                  id="time"
                  is={Text}
                  text={endTime}
                  pattern="paragraph"
                />
              </TimeAndDateItem>
            </TimeAndDateWrapper>
          </>
        )}
        <CTAWrapper>
          <Element
            id="cta-button"
            is={Action}
            element="button"
            text="Buy tickets"
            style={{
              padding: [
                `${rem(8)} ${rem(40)}`,
                `${rem(8)} ${rem(40)}`,
                `${rem(8)} ${rem(40)}`,
              ],
              maxWidth: `${rem(300)}`,
            }}
            action={ACTIONS.registrationPage}
            actions={[ACTIONS.registrationPage, ACTIONS.externalLink]}
            showOptions
          />
        </CTAWrapper>
      </ContentWrapper>
    </BackgroundWrapper>
  );
}

VRHeader.craft = {
  custom: {
    displayName: "VR Header",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: CustomHeaderSettings,
  },
};
